import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Button,
} from "@boozallen-uip/uswds-react-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faEnvelope, faUser } from '@fortawesome/free-solid-svg-icons'
import './Header.scss'
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import gsaStarMark from '../assets/gsa-250-logo.png';
import acrRepoLogo from '../assets/ACRRepo-logo.png';
import { UserContext } from "./UserContext";
import {
  CONTACT_URL,
  FOR_BUYERS_URL,
  FOR_VENDORS_URL,
  HOME_URL,
  RESOURCES_URL,
} from "../utlities/core.constants";
import { NavLinkStyled, SectionContainer } from "./shared.styled";
import AccountLinks from "../components/AccountLinks.component";
import NotificationBell from "../components/NotificationBell.component";
import { useLoginModal } from "../components/modals/LoginModalContext";
import useSiteNavLinks from "../hooks/useSiteNavLinks.hook";

// eslint-disable-next-line react/prefer-stateless-function
export default function HeaderWithLogin() {
  const { user } = useContext(UserContext);
  const { openLoginModal, isLoginModalOpen } = useLoginModal();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);
  const orgDropdownButtonRef = useRef(null);
  const orgMenuItemRefs = useRef([]);
  const shouldFocusFirstOrgItemRef = useRef(false);
  const orgCloseTimeoutRef = useRef(null);
  const orgPointerInsideRef = useRef(false);

  // eslint-disable-next-line class-methods-use-this
  const isActivePath = (url) =>
    location.pathname === url || location.pathname.startsWith(`${url}/`);

  const {
    buyersNavLabel,
    showForVendorsLink,
    showAcrEditorLink,
    acrEditorUrl,
    myOrganizationMenuItems,
    showMyOrganizationLink,
    showSuperUserToolsLink,
    superUserToolsUrl,
  } = useSiteNavLinks();

  const isMyOrganizationActive = myOrganizationMenuItems.some((item) => isActivePath(item.to));

  const cancelScheduledOrgClose = useCallback(() => {
    if (orgCloseTimeoutRef.current) {
      clearTimeout(orgCloseTimeoutRef.current);
      orgCloseTimeoutRef.current = null;
    }
  }, []);

  const openOrgDropdown = useCallback(() => {
    cancelScheduledOrgClose();
    setIsOrgDropdownOpen(true);
  }, [cancelScheduledOrgClose]);

  const closeOrgDropdown = useCallback((returnFocus = false) => {
    cancelScheduledOrgClose();
    setIsOrgDropdownOpen(false);
    if (returnFocus) {
      orgDropdownButtonRef.current?.focus();
    }
  }, [cancelScheduledOrgClose]);

  // Pointer clicks focus the trigger without :focus-visible, so moving the pointer away can
  // still close. Keyboard focus on the trigger, or any focus inside the menu, must keep it
  // open — otherwise mouseleave hides content the keyboard user is still in (WCAG 1.4.13).
  const shouldKeepOrgDropdownOpenForFocus = () => {
    const active = document.activeElement;
    if (!active || !orgDropdownRef.current?.contains(active)) {
      return false;
    }
    if (active === orgDropdownButtonRef.current) {
      return active.matches(":focus-visible");
    }
    return true;
  };

  // A pointer click lands right after onMouseEnter has already opened the menu, so a plain toggle
  // would immediately close it — and it couldn't reopen until the pointer left and re-entered. For
  // mouse clicks (MouseEvent.detail >= 1) keep it open and let hover / mouseleave / outside-click /
  // Escape drive closing; keyboard activation (Enter/Space) reports detail 0 and still toggles.
  const handleOrgDropdownButtonClick = (event) => {
    if (event.detail === 0) {
      cancelScheduledOrgClose();
      setIsOrgDropdownOpen((prevState) => !prevState);
      return;
    }
    openOrgDropdown();
  };

  // A short close delay keeps the menu open while the pointer crosses the gap from the
  // trigger into the menu (or moves diagonally between items), satisfying the "hoverable"
  // condition of WCAG 1.4.13. Escape and focus-out still close it immediately.
  const scheduleCloseOrgDropdown = () => {
    cancelScheduledOrgClose();
    orgCloseTimeoutRef.current = setTimeout(() => {
      orgCloseTimeoutRef.current = null;
      if (orgPointerInsideRef.current || shouldKeepOrgDropdownOpenForFocus()) {
        return;
      }
      setIsOrgDropdownOpen(false);
    }, 300);
  };

  useEffect(() => cancelScheduledOrgClose, []);

  useEffect(() => {
    if (!isOrgDropdownOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        closeOrgDropdown();
      }
    };

    const handleEscape = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      // Hover can open the menu while focus is elsewhere. Dismiss it, but only
      // move focus to the trigger when focus was already inside the dropdown.
      const focusInside = Boolean(orgDropdownRef.current?.contains(document.activeElement));
      closeOrgDropdown(focusInside);
    };

    window.addEventListener('click', handleClickOutside);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOrgDropdownOpen, closeOrgDropdown]);

  useEffect(() => {
    if (isOrgDropdownOpen && shouldFocusFirstOrgItemRef.current) {
      orgMenuItemRefs.current[0]?.focus();
      shouldFocusFirstOrgItemRef.current = false;
    }
  }, [isOrgDropdownOpen]);

  const handleOrgDropdownButtonKeyDown = (event) => {
    if (event.key === "ArrowDown" && myOrganizationMenuItems.length > 0) {
      event.preventDefault();
      cancelScheduledOrgClose();
      if (isOrgDropdownOpen) {
        orgMenuItemRefs.current[0]?.focus();
        return;
      }
      shouldFocusFirstOrgItemRef.current = true;
      openOrgDropdown();
    }
    if (event.key === "ArrowUp" && myOrganizationMenuItems.length > 0) {
      event.preventDefault();
      cancelScheduledOrgClose();
      if (!isOrgDropdownOpen) {
        openOrgDropdown();
      }
      window.requestAnimationFrame(() => {
        orgMenuItemRefs.current[myOrganizationMenuItems.length - 1]?.focus();
      });
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeOrgDropdown(true);
    }
  };

  const handleOrgMenuItemKeyDown = (event, index) => {
    if (!isOrgDropdownOpen || myOrganizationMenuItems.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = (index + 1) % myOrganizationMenuItems.length;
      orgMenuItemRefs.current[nextIndex]?.focus();
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      const prevIndex = (index - 1 + myOrganizationMenuItems.length) % myOrganizationMenuItems.length;
      orgMenuItemRefs.current[prevIndex]?.focus();
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      orgMenuItemRefs.current[0]?.focus();
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      orgMenuItemRefs.current[myOrganizationMenuItems.length - 1]?.focus();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeOrgDropdown(true);
    }
  };

  const handleOrgDropdownBlur = (event) => {
    if (orgDropdownRef.current?.contains(event.relatedTarget)) {
      return;
    }
    // Focus left, but the pointer is still over the trigger or menu. Closing here
    // would hide hover content (WCAG 1.4.13) and leave it stuck closed, because
    // mouseenter does not fire again until the pointer leaves and re-enters.
    if (orgPointerInsideRef.current) {
      return;
    }
    closeOrgDropdown();
  };


  return (
    <div>
      <SectionContainer className="mb-0 pb-0 pt-4">
        <div className="flex justify-between mb-8">
          <div>
            <NavLink to={HOME_URL} className="inline-flex items-start gap-3" aria-label="ACR Repository Home">
              <img src={gsaStarMark} alt="GSA Starmark Flag logo" style={{ height: '56px', width: 'auto' }} />
              <img src={acrRepoLogo} alt="ACR Repository logo" style={{ height: '56px', width: 'auto', marginTop: '-3px' }} />
            </NavLink>
          </div>
          <div>
            <div className="login-buttons ">
              <div className="login-button-contact ">
                <Button unstyled className="login-button-link text-sm py-2 mt-[4px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2" onClick={() => navigate(CONTACT_URL)}><span className="text-primary-darker"><FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />&nbsp;Contact</span></Button>
              </div>
              <div className={user ? "login-button-divider-1" : "login-button-divider-1"}>
                <span className="center py-2">|</span>
              </div>
              {user && (
                <>
                  <div className="login-button-notification flex items-center px-2">
                    <NotificationBell />
                  </div>
                  <div className="login-button-divider-2">
                    <span className="center py-2">|</span>
                  </div>
                </>
              )}
              <div className="login-button flex items-center">
                {!user ?
                <Button unstyled onClick={openLoginModal} aria-haspopup="dialog" aria-controls="login-modal" aria-expanded={isLoginModalOpen} className="login-button-link text-[14px] block px-2 py-2 mb-1 text-white no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"><span className="text-primary-darker"><FontAwesomeIcon className="text-[14px] mt-[4px]" icon={faUser} aria-hidden="true" /> Login</span></Button>
                : (
                  <AccountLinks />
                   )
                }
              </div>
            </div>
          </div>
        </div>
        <nav className="flex font-bold" aria-label="Primary">
          <NavLinkStyled active={isActivePath(HOME_URL).toString()}>
            <NavLink to={HOME_URL}>
              Home
            </NavLink>
          </NavLinkStyled>
          <NavLinkStyled active={isActivePath(FOR_BUYERS_URL).toString()}>
            <NavLink to={FOR_BUYERS_URL}>
              {buyersNavLabel}
            </NavLink>
          </NavLinkStyled>
          {showForVendorsLink ? (
            <NavLinkStyled active={isActivePath(FOR_VENDORS_URL).toString()}>
              <NavLink to={FOR_VENDORS_URL}>
                For Vendors
              </NavLink>
            </NavLinkStyled>
          ) : null}
          {showMyOrganizationLink ? (
            <NavLinkStyled active={isMyOrganizationActive.toString()} className="relative">
              <div
                ref={orgDropdownRef}
                className="relative"
                onBlur={handleOrgDropdownBlur}
                onMouseEnter={() => {
                  orgPointerInsideRef.current = true;
                  openOrgDropdown();
                }}
                onMouseLeave={() => {
                  orgPointerInsideRef.current = false;
                  scheduleCloseOrgDropdown();
                }}
              >
                <button
                  ref={orgDropdownButtonRef}
                  type="button"
                  id="org-dropdown-button"
                  aria-haspopup="true"
                  aria-controls="org-dropdown-menu"
                  aria-expanded={isOrgDropdownOpen}
                  onClick={handleOrgDropdownButtonClick}
                  onKeyDown={handleOrgDropdownButtonKeyDown}
                  className="inline-flex items-center border-0 bg-transparent p-0 text-[14px] font-bold text-gray-darkest cursor-pointer focus:outline-2 focus:outline-primary focus:outline-offset-2"
                >
                  <span>My Organization</span>
                  {/* Inline transform instead of Tailwind's rotate-* utility: this app omits
                      Tailwind's base layer, so the --tw-* transform vars are undefined and
                      transform utilities are dropped by the browser. */}
                  <FontAwesomeIcon
                    icon={faAngleDown}
                    className="ml-2"
                    style={{
                      transition: "transform 200ms ease",
                      transform: isOrgDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id="org-dropdown-menu"
                  aria-labelledby="org-dropdown-button"
                  className={`absolute left-0 top-full mt-2 min-w-[15rem] overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-lighter z-50 ${isOrgDropdownOpen ? "" : "hidden"}`}
                >
                  {myOrganizationMenuItems.map((item, index) => (
                    <NavLink
                      key={item.to}
                      ref={(node) => {
                        orgMenuItemRefs.current[index] = node;
                      }}
                      to={item.to}
                      onClick={() => closeOrgDropdown()}
                      onKeyDown={(event) => handleOrgMenuItemKeyDown(event, index)}
                      className={({ isActive }) =>
                        `block border-l-4 px-4 py-2.5 hover:bg-gray-lightest no-underline transition-colors focus:outline-2 focus:outline-primary focus:outline-offset-[-2px] ${
                          isActive ? "border-primary bg-primary-lighter font-bold" : "border-transparent"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </NavLinkStyled>
          ) : null}
          {showAcrEditorLink ?
          <NavLinkStyled active={isActivePath(acrEditorUrl).toString()}>
            <NavLink to={acrEditorUrl}>
              ACR Editor
            </NavLink>
          </NavLinkStyled>
          : null}
          <NavLinkStyled active={isActivePath(RESOURCES_URL).toString()}>
            <NavLink to={RESOURCES_URL}>
              Resources
            </NavLink>
          </NavLinkStyled>
          {showSuperUserToolsLink ?
          <NavLinkStyled active={isActivePath(superUserToolsUrl).toString()}>
            <NavLink to={superUserToolsUrl}>
              Superuser Tools
            </NavLink>
          </NavLinkStyled>
          : null}
        </nav>
      </SectionContainer>

    </div>
  );
}

